/**
 * 案件詳細ページ「ダッシュボード」タブ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件1件のステータス・健康度・進捗・子チケット一覧を表示する
 * @graph-connects useProjectDetail [calls] 案件単体取得
 * @graph-connects ChildTicketsSection [calls] 子チケット一覧（比率バー付きテーブル）の描画
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useProjectDetail } from '../../../hooks/useProjects.js';
import { HealthBadge } from '../health/index.js';
import { getStatusVariant } from './ChildTicketsModal.js';
import { ChildTicketsSection } from './ChildTicketsModal.js';
import { ProgressBar } from './ProjectTable.js';

/** ダッシュボードの統計カード1枚。 @graph-connects none */
function StatCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
      <div className="mt-1 text-sm font-medium text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}

/** @graph-connects useProjectDetail [calls] 案件単体取得 */
export function ProjectDashboardTab({ pjKey }: { pjKey: string }) {
  const { t } = useTranslation();
  const { data: project, isLoading, error } = useProjectDetail(pjKey);

  if (error) {
    return (
      <p className="pt-6 text-sm text-[var(--color-text-secondary)]">
        {t('projectDetail.loadFailed')}
      </p>
    );
  }
  if (isLoading || !project) {
    return <p className="pt-6 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>;
  }

  return (
    <div className="space-y-6 pt-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={t('projects.fields.status')}
          value={
            <Badge variant={getStatusVariant(project.status)} size="sm">
              {project.status}
            </Badge>
          }
        />
        <StatCard
          label={t('projects.health.title')}
          value={<HealthBadge health={project.health} />}
        />
        <StatCard label={t('projects.fields.pl')} value={project.assignee ?? '-'} />
        <StatCard label={t('projects.fields.devLeader')} value={project.devLeader ?? '-'} />
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
        <div className="mb-2 flex items-center justify-between text-xs text-[var(--color-text-muted)]">
          <span>{t('projects.fields.completionRatio')}</span>
          <span>
            {project.totalActualPoint}/{project.totalPlannedPoint}
          </span>
        </div>
        <ProgressBar ratio={project.completionRatio} />
      </div>

      <div>
        <h2 className="mb-3 text-base font-semibold">{t('projects.childTickets')}</h2>
        <ChildTicketsSection project={project} />
      </div>
    </div>
  );
}
